import { useEffect, useMemo, useRef, useState } from "react";
import { Box, Button, Container, Drawer, Stack, Typography } from "@mui/material";
import TuneRoundedIcon from "@mui/icons-material/TuneRounded";
import { useLocation, useSearchParams } from "react-router-dom";
import StoreTopBar from "../components/Store/StoreTopBar";
import StoreSidebarFilters from "../components/Store/StoreSidebarFilters";
import StoreProductsGrid from "../components/Store/StoreProductsGrid";

function useDebouncedValue(value, delay = 350) {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => clearTimeout(timeout);
  }, [value, delay]);

  return debouncedValue;
}

export default function Store() {
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  const [sections, setSections] = useState([]);
  const [categories, setCategories] = useState([]);
  const [allBrands, setAllBrands] = useState([]);
  const [brands, setBrands] = useState([]);
  const [products, setProducts] = useState([]);
  const [initialProductsLoading, setInitialProductsLoading] = useState(true);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [searchInput, setSearchInput] = useState(searchParams.get("search") || "");

  const selectedSection = searchParams.get("section") || "";
  const selectedCategory = searchParams.get("category") || "";
  const selectedBrand = searchParams.get("brand") || "";
  const search = searchParams.get("search") || "";
  const debouncedSearch = useDebouncedValue(searchInput, 350);

  const requestIdRef = useRef(0);
  const bootstrappedRef = useRef(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname, location.search]);

  useEffect(() => {
    setSearchInput(search);
  }, [search]);

  useEffect(() => {
    const loadInitialData = async () => {
      try {
        const [sectionsRes, categoriesRes, brandsRes] = await Promise.all([
          fetch("/api/store/public/sections/"),
          fetch("/api/store/public/categories/"),
          fetch("/api/store/public/brands/"),
        ]);

        const [sectionsData, categoriesData, brandsData] = await Promise.all([
          sectionsRes.json(),
          categoriesRes.json(),
          brandsRes.json(),
        ]);

        const nextSections = Array.isArray(sectionsData) ? sectionsData : [];
        const nextCategories = Array.isArray(categoriesData) ? categoriesData : [];
        const nextBrands = Array.isArray(brandsData) ? brandsData : [];

        setSections(nextSections);
        setCategories(nextCategories);
        setAllBrands(nextBrands);
        setBrands(nextBrands);
        bootstrappedRef.current = true;
      } catch {
        setSections([]);
        setCategories([]);
        setAllBrands([]);
        setBrands([]);
        bootstrappedRef.current = true;
      }
    };

    loadInitialData();
  }, []);

  useEffect(() => {
    if (!bootstrappedRef.current) return;
    if (!selectedSection) {
      return;
    }

    const controller = new AbortController();

    const loadCategories = async () => {
      setLoadingCategories(true);

      try {
        const res = await fetch(
          `/api/store/public/categories/?section=${encodeURIComponent(selectedSection)}`,
          { signal: controller.signal }
        );
        const data = await res.json();

        if (controller.signal.aborted) return;
        setCategories(Array.isArray(data) ? data : []);
      } catch (error) {
        if (error.name !== "AbortError") {
          setCategories([]);
        }
      } finally {
        if (!controller.signal.aborted) {
          setLoadingCategories(false);
        }
      }
    };

    loadCategories();

    return () => controller.abort();
  }, [selectedSection]);

  useEffect(() => {
    if (!bootstrappedRef.current) return;

    if (!selectedSection && !selectedCategory) {
      setBrands(allBrands);

      if (
        selectedBrand &&
        !allBrands.some((brand) => String(brand.id) === String(selectedBrand))
      ) {
        const nextParams = new URLSearchParams(searchParams);
        nextParams.delete("brand");
        setSearchParams(nextParams, { replace: true });
      }

      return;
    }

    const controller = new AbortController();

    const loadBrands = async () => {
      try {
        const params = new URLSearchParams();

        if (selectedSection) params.set("section", selectedSection);
        if (selectedCategory) params.set("category", selectedCategory);

        const res = await fetch(`/api/store/public/brands/?${params.toString()}`, {
          signal: controller.signal,
        });
        const data = await res.json();

        if (controller.signal.aborted) return;

        const nextBrands = Array.isArray(data) ? data : [];
        setBrands(nextBrands);

        if (
          selectedBrand &&
          !nextBrands.some((brand) => String(brand.id) === String(selectedBrand))
        ) {
          const nextParams = new URLSearchParams(searchParams);
          nextParams.delete("brand");
          setSearchParams(nextParams, { replace: true });
        }
      } catch (error) {
        if (error.name !== "AbortError") {
          setBrands([]);

          if (
            selectedBrand &&
            !allBrands.some((brand) => String(brand.id) === String(selectedBrand))
          ) {
            const nextParams = new URLSearchParams(searchParams);
            nextParams.delete("brand");
            setSearchParams(nextParams, { replace: true });
          }
        }
      }
    };

    loadBrands();

    return () => controller.abort();
  }, [selectedSection, selectedCategory, selectedBrand, allBrands, searchParams, setSearchParams]);

  useEffect(() => {
    const currentUrlSearch = searchParams.get("search") || "";

    if (debouncedSearch === currentUrlSearch) {
      return;
    }

    const params = new URLSearchParams(searchParams);

    if (debouncedSearch.trim()) {
      params.set("search", debouncedSearch.trim());
    } else {
      params.delete("search");
    }

    setSearchParams(params, { replace: true });
  }, [debouncedSearch, searchParams, setSearchParams]);

  useEffect(() => {
    const controller = new AbortController();
    const currentRequestId = ++requestIdRef.current;

    const loadProducts = async () => {
      setInitialProductsLoading(true);

      try {
        const params = new URLSearchParams();

        if (selectedSection) params.set("section", selectedSection);
        if (selectedCategory) params.set("category", selectedCategory);
        if (selectedBrand) params.set("brand", selectedBrand);
        if (search.trim()) params.set("search", search.trim());

        const query = params.toString();
        const url = query
          ? `/api/store/public/products/?${query}`
          : "/api/store/public/products/";

        const res = await fetch(url, { signal: controller.signal });
        const data = await res.json();

        if (controller.signal.aborted) return;
        if (currentRequestId !== requestIdRef.current) return;

        setProducts(Array.isArray(data) ? data : []);
      } catch (error) {
        if (error.name !== "AbortError" && currentRequestId === requestIdRef.current) {
          setProducts([]);
        }
      } finally {
        if (!controller.signal.aborted && currentRequestId === requestIdRef.current) {
          setInitialProductsLoading(false);
        }
      }
    };

    loadProducts();

    return () => controller.abort();
  }, [selectedSection, selectedCategory, selectedBrand, search]);

  const selectedSectionName = useMemo(() => {
    return sections.find((item) => String(item.id) === String(selectedSection))?.name || "";
  }, [sections, selectedSection]);

  const updateParams = (nextValues) => {
    const params = new URLSearchParams(searchParams);

    Object.entries(nextValues).forEach(([key, value]) => {
      if (value === null || value === undefined || value === "") {
        params.delete(key);
      } else {
        params.set(key, value);
      }
    });

    setSearchParams(params, { replace: true });
  };

  const handleSectionChange = (sectionId) => {
    const params = new URLSearchParams();

    if (sectionId) {
      params.set("section", sectionId);
    }

    if (selectedBrand) {
      params.set("brand", selectedBrand);
    }

    if (search.trim()) {
      params.set("search", search.trim());
    }

    setSearchParams(params, { replace: true });
  };

  const handleCategoryChange = (categoryId) => {
    const params = new URLSearchParams(searchParams);

    if (!categoryId) {
      params.delete("category");
    } else {
      params.set("category", categoryId);
    }

    setSearchParams(params, { replace: true });
  };

  const handleBrandChange = (brandId) => {
    updateParams({ brand: brandId });
  };

  const handleSearchChange = (value) => {
    setSearchInput(value);
  };

  const handleClearFilters = () => {
    setSearchInput("");
    setSearchParams({}, { replace: true });
  };

  const sidebarProps = {
    sections,
    categories,
    selectedSection,
    selectedCategory,
    onSectionChange: handleSectionChange,
    onCategoryChange: handleCategoryChange,
    onClear: handleClearFilters,
    loadingCategories,
  };

  return (
    <Box
      sx={{
        minHeight: "100%",
        bgcolor: "#f8f8f8",
        py: { xs: 2.2, md: 3.2 },
      }}
    >
      <Container maxWidth="xl">
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", lg: "260px minmax(0, 1fr)" },
            gap: { xs: 2, md: 2.2 },
            alignItems: "start",
          }}
        >
          <Box
            sx={{
              display: { xs: "none", lg: "block" },
              position: "sticky",
              top: 116,
            }}
          >
            <StoreSidebarFilters {...sidebarProps} />
          </Box>

          <Box sx={{ minWidth: 0 }}>
            <StoreTopBar
              search={searchInput}
              onSearchChange={handleSearchChange}
              brands={brands}
              selectedBrand={selectedBrand}
              onBrandChange={handleBrandChange}
              selectedSectionName={selectedSectionName}
              totalProducts={products.length}
            />

            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{
                mb: 1.8,
                display: { xs: "flex", lg: "none" },
              }}
            >
              <Box>
                <Typography
                  sx={{
                    fontSize: { xs: 22, md: 26 },
                    fontWeight: 800,
                    color: "#111111",
                    lineHeight: 1.05,
                  }}
                >
                  {selectedSectionName || "Productos"}
                </Typography>
              </Box>

              <Button
                onClick={() => setMobileFiltersOpen(true)}
                startIcon={<TuneRoundedIcon />}
                sx={{
                  px: 1.6,
                  py: 0.9,
                  borderRadius: 999,
                  textTransform: "none",
                  fontSize: 13.5,
                  fontWeight: 800,
                  color: "#111111",
                  bgcolor: "#ffffff",
                  border: "1px solid rgba(17,17,17,0.08)",
                  boxShadow: "0 8px 20px rgba(0,0,0,0.04)",
                  "&:hover": {
                    color: "#e50914",
                    bgcolor: "rgba(229,9,20,0.04)",
                    borderColor: "rgba(229,9,20,0.16)",
                  },
                }}
              >
                Filtros
              </Button>
            </Stack>

            <StoreProductsGrid
              products={products}
              loading={initialProductsLoading}
            />
          </Box>
        </Box>
      </Container>

      <Drawer
        anchor="left"
        open={mobileFiltersOpen}
        onClose={() => setMobileFiltersOpen(false)}
        ModalProps={{
          keepMounted: true,
        }}
        PaperProps={{
          sx: {
            width: "88%",
            maxWidth: 340,
            bgcolor: "#f8f8f8",
            p: 1.4,
          },
        }}
      >
        <StoreSidebarFilters {...sidebarProps} />
      </Drawer>
    </Box>
  );
}